import { Button, Checkbox, FieldMessage, Heading, Label, Radio, Select, Surface, Text, TextInput } from '@elladx/design-system';
import { useLayoutEffect, useRef, useState } from 'react';
import type { Parcel } from '../types';
import { DEFAULT_PACKAGE_PRESETS, isUsablePackage, loadPackagePresets, savePackagePreset } from '../utils/packagePresets';
import { parcelSummary } from '../utils/shippingServices';
// FedEx predefined package options
const FEDEX_PACKAGES = [
    { value: '', label: 'Custom Dimensions' },
    { value: 'FedExEnvelope', label: 'FedEx Envelope' },
    { value: 'FedExPak', label: 'FedEx Pak' },
    { value: 'FedExBox', label: 'FedEx Box' },
    { value: 'FedExSmallBox', label: 'FedEx Small Box' },
    { value: 'FedExMediumBox', label: 'FedEx Medium Box' },
    { value: 'FedExLargeBox', label: 'FedEx Large Box' },
    { value: 'FedExExtraLargeBox', label: 'FedEx Extra Large Box' },
    { value: 'FedExTube', label: 'FedEx Tube' },
    { value: 'FedEx10kgBox', label: 'FedEx 10kg Box' },
    { value: 'FedEx25kgBox', label: 'FedEx 25kg Box' },
];
interface ParcelFormProps {
    parcel: Parcel;
    onChange: (parcel: Parcel) => void;
    readOnly?: boolean;
    /** Render just the fields, without the `.form-section` wrapper + `<h2>`. */
    hideHeader?: boolean;
    errors?: Partial<Record<keyof Parcel, string>>;
}
function weightParts(weight?: number): { pounds: string; ounces: string } {
    if (weight === undefined || !Number.isFinite(weight)) return { pounds: '', ounces: '' };
    return { pounds: String(Math.floor(weight / 16)), ounces: String(Number((weight % 16).toFixed(6))) };
}
function ParcelForm({ parcel, onChange, readOnly = false, hideHeader = false, errors = {} }: ParcelFormProps) {
    const [units, setUnits] = useState<'lb' | 'oz'>('lb');
    const [pounds, setPounds] = useState(weightParts(parcel.weight).pounds);
    const [ounces, setOunces] = useState(weightParts(parcel.weight).ounces);
    const emittedWeight = useRef(parcel.weight);
    const [customPresets, setCustomPresets] = useState(loadPackagePresets);
    const [selectedPreset, setSelectedPreset] = useState('custom');
    const [savePreset, setSavePreset] = useState(false);
    const [presetName, setPresetName] = useState('');
    const [presetMessage, setPresetMessage] = useState('');
    const [presetError, setPresetError] = useState('');
    useLayoutEffect(() => {
        if (!Object.is(parcel.weight, emittedWeight.current)) {
            setPounds(weightParts(parcel.weight).pounds);
            setOunces(weightParts(parcel.weight).ounces);
            emittedWeight.current = parcel.weight;
        }
    }, [parcel.weight]);
    const changeMeasurements = (field: keyof Parcel, value: string) => {
        setSelectedPreset('custom');
        setPresetMessage('');
        handleChange(field, value);
    };
    const changeCombinedWeight = (field: 'pounds' | 'ounces', value: string) => {
        const nextPounds = field === 'pounds' ? value : pounds;
        const nextOunces = field === 'ounces' ? value : ounces;
        setPounds(nextPounds);
        setOunces(nextOunces);
        const weight = nextPounds === '' && nextOunces === '' ? undefined
            : Number(nextPounds || 0) < 0 || !Number.isInteger(Number(nextPounds || 0)) || Number(nextOunces || 0) < 0
                ? NaN : Number(nextPounds || 0) * 16 + Number(nextOunces || 0);
        emittedWeight.current = weight;
        setSelectedPreset('custom');
        setPresetMessage('');
        onChange({ ...parcel, weight });
    };
    // An empty field is stored as undefined (never 0), so a stored 0 is always
    // a real typed value and must keep rendering as "0" — `?? ''` below, not
    // `|| ''`, which would erase the keystroke while entering values like 0.5.
    const handleChange = (field: keyof Parcel, value: string) => {
        const numValue = value === '' ? undefined : parseFloat(value);
        onChange({ ...parcel, [field]: numValue });
    };
    const handlePackageChange = (value: string) => {
        if (value) {
            // When selecting a predefined package, clear dimensions
            onChange({
                ...parcel,
                predefined_package: value,
                length: undefined,
                width: undefined,
                height: undefined,
            });
        }
        else {
            // When switching to custom dimensions, clear predefined package
            onChange({
                ...parcel,
                predefined_package: undefined,
            });
        }
    };
    const usingPredefinedPackage = Boolean(parcel.predefined_package);
    const presets = [...DEFAULT_PACKAGE_PRESETS, ...customPresets];
    const choosePreset = (id: string) => {
        setSelectedPreset(id);
        setPresetMessage('');
        setPresetError('');
        const preset = presets.find(item => item.id === id);
        if (preset) onChange({ ...preset.parcel });
        else onChange({ ...parcel, predefined_package: undefined });
    };
    const errorFor = (field: keyof Parcel) => errors[field] ? <FieldMessage id={`parcel-${field}-error`} error>{errors[field]}</FieldMessage> : null;
    const contents = <div className="package-editor">
      <div className="package-presets" role="radiogroup" aria-label="Package presets">
        {presets.map(preset => <Label className={`shipping-choice ${selectedPreset === preset.id ? 'selected' : ''}`} key={preset.id}>
          <Radio name="package-preset" value={preset.id} checked={selectedPreset === preset.id} onChange={() => choosePreset(preset.id)} disabled={readOnly}/>
          <span><strong>{preset.name}</strong><Text as="span" size="caption">{parcelSummary(preset.parcel)}</Text></span>
        </Label>)}
        <Label className={`shipping-choice ${selectedPreset === 'custom' ? 'selected' : ''}`}>
          <Radio name="package-preset" value="custom" checked={selectedPreset === 'custom'} onChange={() => choosePreset('custom')} disabled={readOnly}/>
          <span><strong>Custom package</strong><Text as="span" size="caption">Enter your own measurements</Text></span>
        </Label>
      </div>
      <div className="form-group">
        <Label htmlFor="parcel-package-type">Package type</Label>
        <Select id="parcel-package-type" value={parcel.predefined_package || ''} onChange={event => { setSelectedPreset('custom'); handlePackageChange(event.target.value); }} disabled={readOnly}>
          {FEDEX_PACKAGES.map(pkg => <option key={pkg.value} value={pkg.value}>{pkg.label}</option>)}
        </Select>
      </div>
      <div className="form-group">
        <Label htmlFor="parcel-weight-units">Weight units</Label>
        <Select id="parcel-weight-units" value={units} onChange={event => setUnits(event.target.value as 'lb' | 'oz')} disabled={readOnly}>
          <option value="lb">lb / oz</option><option value="oz">oz</option>
        </Select>
      </div>
      {units === 'lb' ? <div className="shipping-weight-grid">
        <div className="form-group"><Label htmlFor="parcel-pounds">Pounds</Label>
          <TextInput id="parcel-pounds" type="number" min="0" step="1" inputMode="decimal" value={pounds} onChange={event => changeCombinedWeight('pounds', event.target.value)} readOnly={readOnly} invalid={Boolean(errors.weight)} aria-describedby={errors.weight ? 'parcel-weight-error' : 'parcel-equivalent-weight'}/>
        </div>
        <div className="form-group"><Label htmlFor="parcel-ounces">Ounces</Label>
          <TextInput id="parcel-ounces" type="number" min="0" step="0.1" inputMode="decimal" value={ounces} onChange={event => changeCombinedWeight('ounces', event.target.value)} readOnly={readOnly} invalid={Boolean(errors.weight)} aria-describedby={errors.weight ? 'parcel-weight-error' : 'parcel-equivalent-weight'}/>
        </div>
      </div> : <div className="form-group"><Label htmlFor="parcel-weight">Weight (oz) *</Label>
        <TextInput id="parcel-weight" type="number" min="0.1" step="0.1" inputMode="decimal" value={parcel.weight ?? ''} onChange={event => changeMeasurements('weight', event.target.value)} readOnly={readOnly} invalid={Boolean(errors.weight)} aria-describedby={errors.weight ? 'parcel-weight-error' : 'parcel-equivalent-weight'}/>
      </div>}
      <Text id="parcel-equivalent-weight" size="caption">Equivalent weight · {Number.isFinite(parcel.weight) ? parcel.weight : '—'} oz</Text>
      {errorFor('weight')}
      {!usingPredefinedPackage ? <div className="parcel-grid">
        {(['length', 'width', 'height'] as const).map(field => <div className="form-group" key={field}>
          <Label htmlFor={`parcel-${field}`}>{field.charAt(0).toUpperCase() + field.slice(1)} (in)</Label>
          <TextInput id={`parcel-${field}`} type="number" min="0.1" step="0.1" inputMode="decimal" value={parcel[field] ?? ''} onChange={event => changeMeasurements(field, event.target.value)} readOnly={readOnly} invalid={Boolean(errors[field])} aria-describedby={errors[field] ? `parcel-${field}-error` : undefined}/>
          {errorFor(field)}
        </div>)}
      </div> : <Text size="caption">Using predefined package dimensions from FedEx</Text>}
      <Label className="checkbox-label"><Checkbox checked={savePreset} onChange={event => setSavePreset(event.target.checked)} disabled={readOnly}/>Save as a package preset</Label>
      {savePreset && <div className="shipping-save-preset">
        <div className="form-group"><Label htmlFor="parcel-preset-name">Preset name</Label><TextInput id="parcel-preset-name" value={presetName} maxLength={60} onChange={event => setPresetName(event.target.value)} readOnly={readOnly}/></div>
        <Button type="button" variant="secondary" disabled={readOnly || !presetName.trim() || !isUsablePackage(parcel)} onClick={() => {
            const saved = savePackagePreset(presetName, parcel);
            if (!saved) { setPresetError('This preset could not be saved. Check your measurements and allow browser storage.'); return; }
            setCustomPresets(loadPackagePresets()); setSelectedPreset(saved.id); setPresetError(''); setPresetMessage('Package preset saved.');
        }}>Save package preset</Button>
      </div>}
      {presetMessage && <Text role="status">{presetMessage}</Text>}
      {presetError && <Text role="alert" tone="error">{presetError}</Text>}
    </div>;
    return hideHeader ? contents : <Surface className="form-section" aria-labelledby="parcel-heading" as="section"><Heading id="parcel-heading" as="h2" variant="card">Package details</Heading>{contents}</Surface>;
}
export default ParcelForm;
